import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { usePontoTab } from "@/hooks/usePontoTab";
import { QRCodeSVG } from "qrcode.react";
import {
  Clock, LogIn, LogOut, MapPin, Send, MessageCircle, Mail,
  Loader2, AlertTriangle, CheckCircle2, Timer, BarChart3,
  FileText, Copy, Download
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { toast } from "sonner";

export function PontoTab() {
  const { t } = useTranslation();
  const {
    activeClocks, members, loading,
    clockingIn, clockingOut,
    selectedMember, setSelectedMember,
    geoStatus,
    weeklyData,
    showInvite, openInviteDialog, closeInviteDialog,
    inviteMember, setInviteMember,
    inviteMethod, setInviteMethod,
    inviteContact, setInviteContact,
    inviteResult,
    sendingInvite,
    handleClockIn, handleClockOut,
    handleSendInvite,
    getElapsedTime,
    resetInviteForm,
  } = usePontoTab();

  if (loading) return <LoadingScreen />;

  return (
    <div className="space-y-6" data-testid="ponto-tab">
      {/* Clock In Card */}
      <Card className="border-2 border-emerald-200 bg-emerald-50/30">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-lg">{t("ponto.clockIn")}</h3>
              <p className="text-xs text-muted-foreground">{t("ponto.clockInDesc")}</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Select value={selectedMember} onValueChange={setSelectedMember}>
              <SelectTrigger className="flex-1" data-testid="member-select">
                <SelectValue placeholder={t("ponto.selectEmployee")} />
              </SelectTrigger>
              <SelectContent>
                {members.map(m => (
                  <SelectItem key={m.id} value={m.id}>{m.name} ({m.role})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              className="gap-2 bg-emerald-600 hover:bg-emerald-700 min-w-[160px]"
              onClick={handleClockIn}
              disabled={clockingIn}
              data-testid="clock-in-btn"
            >
              {clockingIn ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
              {t("ponto.registerEntry")}
            </Button>
            <Button
              variant="outline"
              className="gap-2 min-w-[140px]"
              onClick={() => openInviteDialog()}
              data-testid="invite-btn"
            >
              <Send className="w-4 h-4" />{t("ponto.sendInvite")}
            </Button>
          </div>
          {geoStatus && (
            <div className="flex items-center gap-2 mt-3 text-xs">
              {geoStatus === "loading" && <><Loader2 className="w-3 h-3 animate-spin" /><span className="text-muted-foreground">{t("ponto.geoLoading")}</span></>}
              {geoStatus === "success" && <><MapPin className="w-3 h-3 text-emerald-600" /><span className="text-emerald-700">{t("ponto.geoSuccess")}</span></>}
              {geoStatus === "error" && <><AlertTriangle className="w-3 h-3 text-amber-500" /><span className="text-amber-600">{t("ponto.geoError")}</span></>}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Active Clocks */}
      <div>
        <h3 className="font-bold text-sm uppercase text-muted-foreground mb-3 flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          {t("ponto.activeEmployees")} ({activeClocks.length})
        </h3>
        {activeClocks.length === 0 ? (
          <Card><CardContent className="py-8 text-center text-muted-foreground">{t("ponto.noActiveClocks")}</CardContent></Card>
        ) : (
          <div className="grid gap-2">
            {activeClocks.map(r => (
              <Card key={r.id} className="border-emerald-200/50" data-testid={`active-clock-${r.member_id?.slice(-6)}`}>
                <CardContent className="p-4 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                      <span className="text-sm font-bold text-emerald-700">{r.member_name?.charAt(0)?.toUpperCase()}</span>
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{r.member_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {t("ponto.entry")}: {new Date(r.clock_in).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                        {r.clock_in_lat && <span className="ml-1">({t("ponto.gpsOk")})</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                      <Timer className="w-3 h-3 mr-1" />{getElapsedTime(r.clock_in)}
                    </Badge>
                    <Button
                      variant="destructive"
                      size="sm"
                      className="gap-1"
                      onClick={() => handleClockOut(r.member_id)}
                      disabled={clockingOut}
                      data-testid={`clock-out-${r.member_id?.slice(-6)}`}
                    >
                      <LogOut className="w-3 h-3" />{t("ponto.exit")}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Weekly Dashboard */}
      {weeklyData.length > 0 && (
        <div>
          <h3 className="font-bold text-sm uppercase text-muted-foreground mb-3 flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />{t("ponto.weeklySummary")}
          </h3>
          <Card className="border-border/50">
            <CardContent className="p-4">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={weeklyData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 8 }}
                    formatter={(value, name) => [`${value}h`, name === "hours" ? t("ponto.normalHours") : t("ponto.overtime")]}
                    labelFormatter={(label) => {
                      const d = weeklyData.find(w => w.day === label);
                      return `${label} (${d?.date || ""}) - ${d?.employees || 0} func.`;
                    }}
                  />
                  <Legend formatter={(v) => v === "hours" ? t("ponto.hoursLabel") : t("ponto.extraLabel")} />
                  <Bar dataKey="hours" fill="#059669" radius={[4, 4, 0, 0]} name="hours" />
                  <Bar dataKey="overtime" fill="#f59e0b" radius={[4, 4, 0, 0]} name="overtime" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Invite Dialog */}
      <Dialog open={showInvite} onOpenChange={(open) => { if (!open) closeInviteDialog(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="uppercase font-bold text-sm">{t("ponto.inviteDialogTitle")}</DialogTitle></DialogHeader>
          {!inviteResult ? (
            <div className="space-y-4">
              <div>
                <Label className="text-xs">{t("ponto.employee")}</Label>
                <Select value={inviteMember} onValueChange={setInviteMember}>
                  <SelectTrigger data-testid="invite-member"><SelectValue placeholder={t("ponto.selectEmployee")} /></SelectTrigger>
                  <SelectContent>{members.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{t("ponto.sendMethod")}</Label>
                <div className="flex gap-2 mt-1">
                  <Button variant={inviteMethod === "whatsapp" ? "default" : "outline"} size="sm" className="gap-1 flex-1" onClick={() => setInviteMethod("whatsapp")} data-testid="invite-whatsapp">
                    <MessageCircle className="w-3.5 h-3.5" />WhatsApp
                  </Button>
                  <Button variant={inviteMethod === "email" ? "default" : "outline"} size="sm" className="gap-1 flex-1" onClick={() => setInviteMethod("email")} data-testid="invite-email">
                    <Mail className="w-3.5 h-3.5" />Email
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-xs">{inviteMethod === "whatsapp" ? t("ponto.whatsappNumber") : t("ponto.emailLabel")}</Label>
                <Input
                  value={inviteContact}
                  onChange={e => setInviteContact(e.target.value)}
                  placeholder={inviteMethod === "whatsapp" ? "+34612345678" : "funcionario@email.com"}
                  data-testid="invite-contact"
                />
              </div>
              <Button
                className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700"
                onClick={handleSendInvite}
                disabled={sendingInvite}
                data-testid="send-invite-btn"
              >
                {sendingInvite ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {inviteMethod === "whatsapp" ? t("ponto.generateWhatsapp") : t("ponto.sendEmailBtn")}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                <CheckCircle2 className="w-6 h-6 text-emerald-600 mb-2" />
                <p className="font-bold text-sm text-emerald-800 mb-1">
                  {inviteResult.method === "whatsapp" ? t("ponto.whatsappGenerated") : t("ponto.emailSent")}
                </p>
                <p className="text-xs text-emerald-700">{t("ponto.employeeLink")}</p>
                <div className="bg-white rounded border p-2 mt-1 flex items-center gap-2">
                  <code className="text-xs flex-1 break-all">{inviteResult.link}</code>
                  <Button variant="ghost" size="sm" className="h-7 shrink-0" onClick={() => { navigator.clipboard.writeText(inviteResult.link); toast.success(t("ponto.copied")); }}>
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
              </div>
              <div className="text-center" id="qr-print-area">
                <p className="text-xs font-bold text-muted-foreground uppercase mb-2">{t("ponto.qrPrintHint")}</p>
                <div className="bg-white border-2 border-dashed border-emerald-300 rounded-lg p-6 inline-block">
                  <QRCodeSVG value={inviteResult.link} size={180} level="H" includeMargin />
                  <p className="text-xs text-muted-foreground mt-2 font-medium">{members.find(m => m.id === inviteMember)?.name || ""}</p>
                  <p className="text-[10px] text-muted-foreground">{t("ponto.qrScanHint")}</p>
                </div>
              </div>
              <Button variant="outline" className="w-full gap-2" onClick={() => {
                const qrEl = document.getElementById("qr-print-area");
                const iframe = document.createElement("iframe");
                iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
                document.body.appendChild(iframe);
                const doc = iframe.contentWindow.document;
                doc.open();
                doc.write(`<html><head><title>${t("ponto.printQr")}</title><style>body{text-align:center;font-family:Arial;padding:40px} p{margin:4px 0}</style></head><body><h2>${t("ponto.qrDocTitle")}</h2>${qrEl.innerHTML}<p style="margin-top:20px;font-size:10px;color:#999">Kaiso System</p></body></html>`);
                doc.close();
                iframe.contentWindow.focus();
                iframe.contentWindow.print();
                setTimeout(() => { try { document.body.removeChild(iframe); } catch {} }, 3000);
              }} data-testid="print-qr-btn">
                <FileText className="w-4 h-4" />{t("ponto.printQr")}
              </Button>
              <Button variant="outline" className="w-full gap-2" onClick={() => {
                const svg = document.querySelector("#qr-print-area svg");
                if (!svg) return;
                const svgData = new XMLSerializer().serializeToString(svg);
                const canvas = document.createElement("canvas");
                const size = 300;
                canvas.width = size; canvas.height = size;
                const ctx = canvas.getContext("2d");
                const img = new Image();
                img.onload = () => {
                  ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, size, size);
                  ctx.drawImage(img, 0, 0, size, size);
                  const a = document.createElement("a");
                  const memberName = members.find(m => m.id === inviteMember)?.name || "qr-ponto";
                  a.download = `qr-ponto-${memberName.replace(/\s+/g, "-").toLowerCase()}.png`;
                  a.href = canvas.toDataURL("image/png");
                  a.click();
                };
                img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
              }} data-testid="download-qr-btn">
                <Download className="w-4 h-4" />{t("ponto.downloadQr")}
              </Button>
              {inviteResult.method === "whatsapp" && inviteContact && (
                <a
                  href={`https://wa.me/${inviteContact.replace(/\D/g, "")}?text=${encodeURIComponent(`Olá! Aqui está o link para registar o seu ponto:\n${inviteResult.link}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-2 px-4 rounded-lg bg-[#25D366] text-white font-semibold text-sm hover:bg-[#1ebe5a] transition"
                >
                  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.116 1.523 5.847L.057 23.886a.5.5 0 0 0 .611.611l6.054-1.463A11.945 11.945 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.86 0-3.6-.5-5.1-1.37l-.365-.213-3.787.915.935-3.737-.237-.385A9.939 9.939 0 0 1 2 12c0-5.514 4.486-10 10-10s10 4.486 10 10-4.486 10-10 10z"/></svg>
                  {t("ponto.sendWhatsappBtn")}
                </a>
              )}
              {inviteResult.sent && (
                <p className="text-xs text-center text-emerald-600">{t("ponto.autoSent")}</p>
              )}
              <Button variant="outline" className="w-full" onClick={resetInviteForm}>{t("ponto.sendAnother")}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
